import { forwardRef, type ReactNode, type CSSProperties, type ButtonHTMLAttributes } from 'react';

export interface ListItemProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Item content */
  children: ReactNode;
  /** Content to display on the right side */
  rightContent?: ReactNode;
  /** Whether the item is disabled */
  disabled?: boolean | undefined;
  /** Size variant */
  size?: 'sm' | 'md' | undefined;
  /** Whether this item is currently active/highlighted (keyboard navigation) */
  isActive?: boolean | undefined;
  /** Whether this item is selected (for select/listbox components) */
  isSelected?: boolean | undefined;
  /** Custom color variant */
  variant?: 'default' | 'destructive' | undefined;
  /** Aria role override */
  role?: string | undefined;
  /** Additional className for outer container */
  className?: string | undefined;
  /** Additional className for inner container */
  innerClassName?: string | undefined;
}

// Size styles using CSS variables with fallbacks
const sizeStyles: {
  sm: { outer: CSSProperties; inner: CSSProperties; content: CSSProperties };
  md: { outer: CSSProperties; inner: CSSProperties; content: CSSProperties };
} = {
  sm: {
    outer: {
      paddingTop: 'var(--uiv2-list-item-sm-outer-py, 1px)',
      paddingBottom: 'var(--uiv2-list-item-sm-outer-py, 1px)',
      paddingLeft: 'var(--uiv2-list-item-sm-outer-px, 6px)',
      paddingRight: 'var(--uiv2-list-item-sm-outer-px, 6px)',
    },
    inner: {
      paddingTop: 'var(--uiv2-list-item-sm-inner-py, 8px)',
      paddingBottom: 'var(--uiv2-list-item-sm-inner-py, 8px)',
      paddingLeft: 'var(--uiv2-list-item-sm-inner-px, 10px)',
      paddingRight: 'var(--uiv2-list-item-sm-inner-px, 10px)',
      gap: 'var(--uiv2-list-item-sm-inner-gap, 8px)',
      borderRadius: 'var(--uiv2-list-item-sm-inner-radius, 6px)',
      fontSize: 'var(--uiv2-list-item-sm-font-size, 14px)',
    },
    content: {
      gap: 'var(--uiv2-list-item-sm-content-gap, 8px)',
    },
  },
  md: {
    outer: {
      paddingTop: 'var(--uiv2-list-item-md-outer-py, 1px)',
      paddingBottom: 'var(--uiv2-list-item-md-outer-py, 1px)',
      paddingLeft: 'var(--uiv2-list-item-md-outer-px, 6px)',
      paddingRight: 'var(--uiv2-list-item-md-outer-px, 6px)',
    },
    inner: {
      paddingTop: 'var(--uiv2-list-item-md-inner-py, 10px)',
      paddingBottom: 'var(--uiv2-list-item-md-inner-py, 10px)',
      paddingLeft: 'var(--uiv2-list-item-md-inner-px, 10px)',
      paddingRight: 'var(--uiv2-list-item-md-inner-px, 10px)',
      gap: 'var(--uiv2-list-item-md-inner-gap, 8px)',
      borderRadius: 'var(--uiv2-list-item-md-inner-radius, 6px)',
      fontSize: 'var(--uiv2-list-item-md-font-size, 14px)',
    },
    content: {
      gap: 'var(--uiv2-list-item-md-content-gap, 8px)',
    },
  },
};

/**
 * ListItem - base primitive for list items
 *
 * Used as the foundation for MenuItem, Select options, Combobox items, etc.
 * Handles common styling, sizing, hover/active states.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ListItem onClick={() => console.log('clicked')}>Item</ListItem>
 *
 * // Selected state (for Select/Listbox)
 * <ListItem isSelected role="option">Selected Item</ListItem>
 *
 * // Destructive variant (for Menu)
 * <ListItem variant="destructive">Delete</ListItem>
 *
 * // With right content
 * <ListItem rightContent={<CheckIcon />}>
 *   Option
 * </ListItem>
 * ```
 */
export const ListItem = forwardRef<HTMLButtonElement, ListItemProps>(
  (
    {
      children,
      rightContent,
      disabled = false,
      size = 'md',
      isActive = false,
      isSelected = false,
      variant = 'default',
      role,
      className = '',
      innerClassName = '',
      ...props
    },
    ref
  ) => {
    const styles = sizeStyles[size] ?? sizeStyles.md;

    const getInnerColorClasses = () => {
      if (disabled) {
        return 'text-uiv2-text-quaternary cursor-not-allowed';
      }
      if (variant === 'destructive') {
        return 'text-red-600 hover:bg-red-50';
      }
      if (isSelected) {
        return 'bg-uiv2-primary-50 text-uiv2-primary-700 font-medium';
      }
      if (isActive) {
        return 'bg-gray-100 text-uiv2-text-secondary';
      }
      return 'text-uiv2-text-secondary hover:bg-gray-100';
    };

    return (
      <div className={`flex items-center ${className}`} style={styles.outer}>
        <button
          ref={ref}
          type="button"
          role={role}
          disabled={disabled}
          aria-selected={isSelected ? true : undefined}
          className={`w-full flex flex-col text-left focus:outline-none transition-colors font-medium ${getInnerColorClasses()} ${innerClassName}`}
          style={styles.inner}
          {...props}
        >
          <div className="flex flex-row items-center w-full" style={styles.content}>
            <span className="flex-1 flex items-center gap-2 min-w-0">{children}</span>
            {rightContent ? <div className="shrink-0 ml-auto">{rightContent}</div> : null}
          </div>
        </button>
      </div>
    );
  }
);

ListItem.displayName = 'ListItem';
